<script setup lang="ts">
    import { nextTick, ref, useAttrs } from 'vue';
    import TextareaInput from './TextareaInput.vue';
    import IconButton from './IconButton.vue';
    import { BoldIcon, ItalicIcon, ListIcon } from '@lucide/vue';
    import { __ } from '@/js/stores/expressions';

    const model = defineModel<string | undefined>();

    defineOptions({ inheritAttrs: false });

    const attrs = useAttrs();

    const countLeading = (s: string, char: string) => {
        let n = 0;
        while (s[n] === char) n++;
        return n;
    };
    const countTrailing = (s: string, char: string) => {
        let n = 0,
            i = s.length - 1;
        while (i >= 0 && s[i] === char) {
            n++;
            i--;
        }
        return n;
    };
    const hasSyntax = (leading: number, trailing: number, len: number) =>
        len === 1 ? leading % 2 === 1 && trailing % 2 === 1 : leading >= len && trailing >= len;

    const textareaRef = ref<InstanceType<typeof TextareaInput> | null>(null);

    const getEl = () => textareaRef.value?.textarea ?? null;

    const findMarker = (
        value: string,
        from: number,
        len: number,
        syntax: string,
        direction: 1 | -1
    ): number => {
        for (let i = from; i >= 0 && i <= value.length - len; i += direction) {
            if (value.substring(i, i + len) !== syntax) continue;
            if (len === 1 && (value[i - 1] === syntax[0] || value[i + 1] === syntax[0])) continue;
            return i;
        }
        return -1;
    };

    const triggerResize = (el: HTMLTextAreaElement) =>
        el.dispatchEvent(new Event('input', { bubbles: true }));

    const unwrapAtCursor = (
        el: HTMLTextAreaElement,
        value: string,
        start: number,
        syntax: string
    ) => {
        const len = syntax.length;

        const isEmptyBlock =
            value.substring(start - len, start) === syntax &&
            value.substring(start, start + len) === syntax &&
            value[start - len - 1] !== syntax[0] &&
            value[start + len] !== syntax[syntax.length - 1];

        if (isEmptyBlock) {
            model.value = value.substring(0, start - len) + value.substring(start + len);
            nextTick(() => {
                el.focus();
                el.setSelectionRange(start - len, start - len);
                triggerResize(el);
            });
            return;
        }

        const closePos = findMarker(value, start, len, syntax, 1);
        const openPos = findMarker(value, start - len, len, syntax, -1);

        if (openPos !== -1 && closePos !== -1) {
            const withoutClose = value.substring(0, closePos) + value.substring(closePos + len);
            model.value =
                withoutClose.substring(0, openPos) + withoutClose.substring(openPos + len);
            nextTick(() => {
                el.focus();
                el.setSelectionRange(start - len, start - len);
                triggerResize(el);
            });
            return;
        }

        model.value = value.substring(0, start) + syntax + syntax + value.substring(start);
        nextTick(() => {
            el.focus();
            el.setSelectionRange(start + len, start + len);
            triggerResize(el);
        });
    };

    const wrapSelection = (syntax: string) => {
        const el = getEl();
        if (!el) return;

        const start = el.selectionStart;
        const end = el.selectionEnd;
        const value = model.value ?? '';
        const len = syntax.length;
        const char = syntax[0];

        if (start === end) {
            unwrapAtCursor(el, value, start, syntax);
            return;
        }

        const selected = value.substring(start, end);
        const selectionHasSyntax = hasSyntax(
            countLeading(selected, char),
            countTrailing(selected, char),
            len
        );
        const surroundingHasSyntax = hasSyntax(
            countTrailing(value.substring(0, start), char),
            countLeading(value.substring(end), char),
            len
        );

        if (selectionHasSyntax) {
            const inner = selected.slice(len, -len);
            model.value = value.substring(0, start) + inner + value.substring(end);
            nextTick(() => {
                el.focus();
                el.setSelectionRange(start, start + inner.length);
                triggerResize(el);
            });
        } else if (surroundingHasSyntax) {
            model.value = value.substring(0, start - len) + selected + value.substring(end + len);
            nextTick(() => {
                el.focus();
                el.setSelectionRange(start - len, end - len);
                triggerResize(el);
            });
        } else {
            model.value =
                value.substring(0, start) + syntax + selected + syntax + value.substring(end);
            nextTick(() => {
                el.focus();
                el.setSelectionRange(start + len, end + len);
                triggerResize(el);
            });
        }
    };

    const handleEnter = (e: KeyboardEvent) => {
        const el = getEl();
        if (!el) return;

        const start = el.selectionStart;
        const value = model.value ?? '';
        const lineStart = value.lastIndexOf('\n', start - 1) + 1;
        const lineContent = value.substring(lineStart, start);

        if (!lineContent.startsWith('- ')) return;

        e.preventDefault();

        if (lineContent === '- ') {
            model.value = value.substring(0, lineStart) + value.substring(start);
            nextTick(() => {
                el.focus();
                el.setSelectionRange(lineStart, lineStart);
                triggerResize(el);
            });
        } else {
            model.value = value.substring(0, start) + '\n- ' + value.substring(start);
            nextTick(() => {
                el.focus();
                el.setSelectionRange(start + 3, start + 3);
                triggerResize(el);
            });
        }
    };

    const toggleBulletList = () => {
        const el = getEl();
        if (!el) return;

        const start = el.selectionStart;
        const end = el.selectionEnd;
        const value = model.value ?? '';
        const lineStart = value.lastIndexOf('\n', start - 1) + 1;
        const hasBullet = value.substring(lineStart, lineStart + 2) === '- ';

        if (hasBullet) {
            model.value = value.substring(0, lineStart) + value.substring(lineStart + 2);
            nextTick(() => {
                el.focus();
                el.setSelectionRange(Math.max(lineStart, start - 2), Math.max(lineStart, end - 2));
                triggerResize(el);
            });
        } else {
            model.value = value.substring(0, lineStart) + '- ' + value.substring(lineStart);
            nextTick(() => {
                el.focus();
                el.setSelectionRange(start + 2, end + 2);
                triggerResize(el);
            });
        }
    };
</script>

<template>
    <div
        class="w-fit max-w-full flex flex-col rounded-sm custom-focus-within"
        :class="$attrs.class"
    >
        <div
            class="w-full flex gap-0.5 px-1 pt-1 pb-0.5 border border-b-0 rounded-t-sm border-gray-light bg-gray-50"
        >
            <IconButton
                type="button"
                :tooltip="__('markdown-toolbar-bold')"
                size="size-6"
                area-size="size-7"
                @mousedown.prevent
                @click="wrapSelection('**')"
            >
                <BoldIcon class="size-4" />
            </IconButton>
            <IconButton
                type="button"
                :tooltip="__('markdown-toolbar-italic')"
                size="size-6"
                area-size="size-7"
                @mousedown.prevent
                @click="wrapSelection('*')"
            >
                <ItalicIcon class="size-4" />
            </IconButton>
            <IconButton
                type="button"
                :tooltip="__('markdown-toolbar-list')"
                size="size-6"
                area-size="size-7"
                @mousedown.prevent
                @click="toggleBulletList"
            >
                <ListIcon class="size-4" />
            </IconButton>
        </div>
        <TextareaInput
            ref="textareaRef"
            v-model="model"
            v-bind="attrs"
            class="rounded-t-none border-t-0 shadow-none focus-visible:outline-none"
            @keydown.enter="handleEnter"
        />
    </div>
</template>
